// Clean form inputs: uppercase grey label above (red * when required), simple rounded box,
// no helper text below (errors show as a red border). Shares the accent colour with fieldTheme.
import { Autocomplete, Box, InputAdornment, TextField, Typography, createFilterOptions } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FIELD } from "./fieldTheme";

// Label above the box
const formLabelSx = {
    display: "block",
    mb: 0.9,
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "#4b5563",
};

// Box style
const boxSx = (error) => ({
    height: 42,
    borderRadius: 2.5,
    bgcolor: "#fff",
    fontSize: 14,
    color: "#111827",
    transition: "box-shadow .15s ease",
    "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: error ? "#ef4444" : "#e5e7eb" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: error ? "#ef4444" : "#cbd5e1" },
    "&.Mui-focused": { boxShadow: `0 0 0 4px ${error ? "rgba(239,68,68,0.12)" : FIELD.focusRing}` },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: error ? "#ef4444" : FIELD.accent },
    "& input::placeholder": { color: "#9ca3af", opacity: 1 },
    // Hide the browser's light-blue autofill background (keep the box white)
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
        WebkitBoxShadow: "0 0 0 100px #fff inset",
        WebkitTextFillColor: "#111827",
        caretColor: "#111827",
        transition: "background-color 9999s ease-out 0s",
    },
});

const StartIcon = ({ icon, active }) =>
    icon ? (
        <InputAdornment
            position="start"
            sx={{ ml: 0, mr: 1, color: active ? FIELD.accent : "#9ca3af", transition: "color .15s ease", "& svg": { fontSize: 19 } }}
        >
            {icon}
        </InputAdornment>
    ) : null;

const Label = ({ htmlFor, label, required }) => (
    <Typography component="label" htmlFor={htmlFor} sx={formLabelSx}>
        {label}
        {required && (
            <Box component="span" sx={{ color: "#ef4444", ml: 0.5 }}>
                *
            </Box>
        )}
    </Typography>
);

// Read-only / disabled box: light grey, readable text
const disabledSx = {
    "&.Mui-disabled": { bgcolor: "#f8fafc" },
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#eef2f6" },
    "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#64748b" },
};

/** Text input. onChange receives the native event (name/value), like a normal TextField. */
export function FieldTextField({ name, label, value, onChange, placeholder, error, maxLength, required, icon, onKeyDown, disabled, type, multiline, rows }) {
    return (
        <Box>
            <Label htmlFor={`${name}-input`} label={label} required={required} />
            <TextField
                id={`${name}-input`}
                name={name}
                fullWidth
                size="small"
                type={type}
                multiline={multiline}
                rows={rows}
                value={value || ""}
                onChange={onChange}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                error={Boolean(error)}
                disabled={disabled}
                autoComplete="off" // no browser autofill suggestions for these fields
                inputProps={maxLength ? { maxLength } : undefined}
                InputProps={icon ? { startAdornment: <StartIcon icon={icon} active={Boolean(value)} /> } : undefined}
                sx={{ "& .MuiOutlinedInput-root": { ...boxSx(error), ...(multiline ? { height: "auto", alignItems: "flex-start", py: 1.25 } : {}), ...disabledSx } }}
            />
        </Box>
    );
}

/**
 * Searchable multi select with checkboxes + "Select all".
 * Same contract as MultiSelectWithAll: value = array of option objects, onChange(name, optionObjects).
 */
export function FieldMultiSelect({ name, label, options = [], value = [], onChange, placeholder, error, disabled, required, icon }) {
    const filterOptions = createFilterOptions({ limit: 100, matchFrom: "any", stringify: (o) => o.label });
    const selected = Array.isArray(value) ? options.filter((o) => value.some((v) => v.value === o.value)) : [];
    const allSelected = options.length > 0 && selected.length === options.length;
    const ALL = { value: "__all__", label: "Select all" };

    return (
        <Box>
            <Label htmlFor={`${name}-multi`} label={label} required={required} />
            <Autocomplete
                id={`${name}-multi`}
                multiple
                disableCloseOnSelect
                options={options.length > 1 ? [ALL, ...options] : options}
                value={selected}
                onChange={(_, opts, reason, details) => {
                    if (details?.option?.value === ALL.value) {
                        onChange(name, allSelected ? [] : options);
                        return;
                    }
                    onChange(name, opts.filter((o) => o.value !== ALL.value));
                }}
                filterOptions={filterOptions}
                getOptionLabel={(o) => o?.label || ""}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                disabled={disabled}
                popupIcon={<KeyboardArrowDownIcon />}
                noOptionsText="No matches"
                size="small"
                renderValue={() => null} // no chips in the field — a count is shown instead
                renderOption={(props, option, { selected: isSel }) => {
                    const { key, ...rest } = props;
                    const checked = option.value === ALL.value ? allSelected : isSel;
                    return (
                        <li key={key} {...rest}>
                            <Box
                                component="span"
                                sx={{
                                    width: 16, height: 16, mr: 1.25, flexShrink: 0, borderRadius: 1,
                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    border: `1.5px solid ${checked ? FIELD.accent : "#cbd5e1"}`,
                                    bgcolor: checked ? FIELD.accent : "#fff", color: "#fff", fontSize: 11, fontWeight: 800,
                                }}
                            >
                                {checked ? "✓" : ""}
                            </Box>
                            <Box component="span" sx={{ fontWeight: option.value === ALL.value ? 700 : "inherit" }}>{option.label}</Box>
                        </li>
                    );
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.75,
                            borderRadius: 2.5,
                            border: "1px solid #e5e7eb",
                            boxShadow: "0 16px 32px -16px rgba(15,23,42,0.3)",
                            "& .MuiAutocomplete-listbox": { p: 0.5, maxHeight: 260 },
                            "& .MuiAutocomplete-option": {
                                borderRadius: 2,
                                fontSize: 13.5,
                                minHeight: "34px !important",
                                py: "4px !important",
                                color: "#334155",
                                "&.Mui-focused": { bgcolor: "#f3f4f6" },
                                '&[aria-selected="true"], &[aria-selected="true"].Mui-focused': { bgcolor: FIELD.selectedBg, color: FIELD.selectedText, fontWeight: 600 },
                            },
                        },
                    },
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={selected.length ? "" : placeholder}
                        error={Boolean(error)}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    <StartIcon icon={icon} active={selected.length > 0} />
                                    {/* selected count instead of chips; the list's checkboxes show which ones */}
                                    {selected.length > 0 && (
                                        <Box
                                            component="span"
                                            title={selected.map((o) => o.label).join(", ")}
                                            sx={{
                                                flexShrink: 0,
                                                mr: 1,
                                                px: 1,
                                                height: 24,
                                                borderRadius: 1.5,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                fontSize: 13,
                                                fontWeight: 700,
                                                whiteSpace: "nowrap",
                                                bgcolor: disabled ? "#eef2f6" : FIELD.selectedBg,
                                                color: disabled ? "#64748b" : FIELD.selectedText,
                                            }}
                                        >
                                            {allSelected && options.length > 1 ? `All (${selected.length})` : `${selected.length} selected`}
                                        </Box>
                                    )}
                                </>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                ...boxSx(error),
                                py: "0 !important",
                                pl: "14px !important",
                                flexWrap: "nowrap",
                                "& .MuiAutocomplete-input": { py: "0 !important", pl: "0 !important", minWidth: 30 },
                                ...disabledSx,
                            },
                            "& .MuiAutocomplete-popupIndicator": { color: "#4b5563" },
                        }}
                    />
                )}
            />
        </Box>
    );
}

/** Searchable single select. onChange(name, value) — same contract as SingleSelect. */
export function FieldSelect({ name, label, options = [], value, onChange, placeholder, error, disabled, required, icon }) {
    const filterOptions = createFilterOptions({ limit: 50, matchFrom: "any", stringify: (o) => o.label });
    const selected = options.find((o) => o.value === value) || null;

    return (
        <Box>
            <Label htmlFor={`${name}-select`} label={label} required={required} />
            <Autocomplete
                id={`${name}-select`}
                options={options}
                value={selected}
                onChange={(_, opt) => onChange(name, opt?.value || "")}
                filterOptions={filterOptions}
                getOptionLabel={(o) => o?.label || ""}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                disabled={disabled}
                disableClearable={!selected}
                popupIcon={<KeyboardArrowDownIcon />}
                noOptionsText="No matches"
                size="small"
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.75,
                            borderRadius: 2.5,
                            border: "1px solid #e5e7eb",
                            boxShadow: "0 16px 32px -16px rgba(15,23,42,0.3)",
                            "& .MuiAutocomplete-listbox": { p: 0.5, maxHeight: 260 },
                            "& .MuiAutocomplete-option": {
                                borderRadius: 2,
                                my: 0, // no gap between options
                                fontSize: 13.5,
                                minHeight: "32px !important",
                                py: "4px !important",
                                lineHeight: 1.4,
                                color: "#334155",
                                "&.Mui-focused": { bgcolor: "#f3f4f6" },
                                '&[aria-selected="true"], &[aria-selected="true"].Mui-focused': {
                                    bgcolor: FIELD.selectedBg,
                                    color: FIELD.selectedText,
                                    fontWeight: 600,
                                },
                            },
                        },
                    },
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={placeholder}
                        error={Boolean(error)}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: <StartIcon icon={icon} active={Boolean(selected)} />,
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                ...boxSx(error),
                                py: "0 !important",
                                pl: "14px !important", // same left padding as the text fields
                                "& .MuiAutocomplete-input": { py: "0 !important", pl: "0 !important" },
                            },
                            "& .MuiAutocomplete-popupIndicator": { color: "#4b5563" },
                        }}
                    />
                )}
            />
        </Box>
    );
}
